import { useEffect, useState } from "react";
import type { TaskItem } from "@edge/shared";
import type { PersonalTaskInput } from "../../hooks/usePersonalTasks";

export interface PersonalTaskEditDialogProps {
  task: TaskItem;
  /** Throws on failure — dialog stays open with error. */
  onSave: (id: string, patch: PersonalTaskInput) => Promise<void> | void;
  onCancel: () => void;
}

/**
 * Edit a MY LIST (personal D1) task — subject + due_date + priority. Mirrors
 * `CrmTaskEditDialog` so the macro Tasks modal has a single visual idiom for
 * both task sources. Personal tasks have no Zoho hop, so the "WRITING…"
 * state is short and the button reads SAVE rather than SAVE TO ZOHO.
 */
export function PersonalTaskEditDialog({
  task,
  onSave,
  onCancel,
}: PersonalTaskEditDialogProps) {
  const [subject, setSubject] = useState(task.subject);
  const [dueDate, setDueDate] = useState(task.dueDate ?? "");
  const initialPriority: "high" | "normal" | "low" =
    task.priority === "high" || task.priority === "low" ? task.priority : "normal";
  const [priority, setPriority] = useState<"high" | "normal" | "low">(initialPriority);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !running) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, running]);

  const handleSave = async () => {
    const trimmed = subject.trim();
    if (!trimmed) {
      setError("subject_required");
      return;
    }
    setError(null);
    setRunning(true);
    try {
      await onSave(task.id, {
        subject: trimmed,
        due_date: dueDate ? dueDate : null,
        priority,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "write_failed");
      setRunning(false);
    }
  };

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card crm-create-card">
        <div className="write-confirm-title">Edit MY LIST task</div>
        <div className="crm-create-body">
          <label className="crm-create-field">
            <span className="crm-create-label">Subject</span>
            <input
              className="crm-create-input"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              autoFocus
            />
          </label>
          <div className="crm-create-row">
            <label className="crm-create-field crm-create-field-half">
              <span className="crm-create-label">Due date</span>
              <input
                className="crm-create-input"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </label>
            <label className="crm-create-field crm-create-field-half">
              <span className="crm-create-label">Priority</span>
              <select
                className="crm-create-input"
                value={priority}
                onChange={(e) =>
                  setPriority(e.target.value as "high" | "normal" | "low")
                }
              >
                <option value="high">HIGH</option>
                <option value="normal">NORMAL</option>
                <option value="low">LOW</option>
              </select>
            </label>
          </div>
        </div>
        {error && <div className="write-confirm-error">⚠ {error}</div>}
        <div className="write-confirm-actions">
          <button
            className="write-confirm-cancel"
            onClick={onCancel}
            disabled={running}
          >
            CANCEL
          </button>
          <button
            className="write-confirm-confirm"
            onClick={handleSave}
            disabled={running}
          >
            {running ? "SAVING…" : "SAVE"}
          </button>
        </div>
      </div>
    </div>
  );
}
